import React from "react";
import { Card, CardContent, Box, Typography, Chip } from "@mui/material";
import {
  Store,
  LocalOffer,
  AccessTime,
  Visibility,
  Share as ShareIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Offer } from "../../types/offers";
import {
  getCategoryConfig,
  formatDate,
  getDaysRemaining,
  isExpiringSoon,
} from "../../utils/offerHelpers";
import { boxShadows, transparentColors } from "../../utils/theme";

interface OfferCardProps {
  offer: Offer;
}

const OfferCard: React.FC<OfferCardProps> = ({ offer }) => {
  const navigate = useNavigate();
  const categoryConfig = getCategoryConfig(offer.category);
  const Icon = categoryConfig.icon;
  const validDate = offer.validUntil || new Date().toISOString();
  const daysRemaining = getDaysRemaining(validDate);
  const expiringSoon = isExpiringSoon(validDate);

  const handleCardClick = () => {
    // Navigate to offer details with mallId and offerId
    navigate(`/mall/${offer.mallId}/offers/${offer.id}`);
  };

  return (
    <Card
      component={motion.div}
      whileHover={{
        y: -6,
        boxShadow: boxShadows.cardHover,
        transition: { duration: 0.3 },
      }}
      onClick={handleCardClick}
      sx={{
        height: "100%",
        borderRadius: { xs: 2.5, sm: 3, md: 3.5 },
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: boxShadows.card,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        border: "1px solid",
        borderColor: "divider",
        "&:hover": {
          borderColor: "primary.light",
          "& .offer-header": {
            transform: "scale(1.02)",
          },
        },
      }}
    >
      {/* Store Name Header with Category - Category-Specific Colors for Better UX */}
      <Box
        className="offer-header"
        sx={{
          background: categoryConfig.gradient,
          p: { xs: 1.5, sm: 2, md: 2.5 },
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1, sm: 1.5 },
          boxShadow: boxShadows.card,
          transition: "all 0.3s ease",
        }}
      >
        {/* Store Name with Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1 },
            flex: 1,
            minWidth: 0,
          }}
        >
          <Store
            sx={{
              fontSize: { xs: 18, sm: 20, md: 22 },
              color: "white",
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              color: "white",
              fontSize: { xs: "0.938rem", sm: "1rem", md: "1.063rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              letterSpacing: "0.3px",
            }}
          >
            {offer.storeName}
          </Typography>
        </Box>

        {/* Category Chip */}
        <Chip
          icon={<Icon sx={{ fontSize: { xs: 13, sm: 14, md: 15 } }} />}
          label={categoryConfig.label}
          size="small"
          sx={{
            backgroundColor: transparentColors.white20,
            backdropFilter: "blur(10px)",
            color: "white",
            fontWeight: 700,
            fontSize: { xs: "0.688rem", sm: "0.75rem", md: "0.813rem" },
            height: { xs: 24, sm: 26, md: 28 },
            flexShrink: 0,
            border: "1px solid",
            borderColor: transparentColors.white20,
            "& .MuiChip-icon": {
              color: "white",
            },
          }}
        />
      </Box>

      <CardContent
        sx={{
          p: { xs: 1.5, md: 2 },
          pt: { xs: 1.25, md: 1.5 },
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Offer Title with Tap Indicator */}
        <Box
          sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1 }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: { xs: "0.938rem", md: "1.063rem" },
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: { xs: "2.4rem", md: "2.7rem" },
              flexGrow: 1,
            }}
          >
            {offer.title}
          </Typography>
          <Visibility
            sx={{
              fontSize: { xs: 16, md: 18 },
              color: "primary.main",
              opacity: 0.7,
              flexShrink: 0,
              mt: 0.25,
            }}
          />
        </Box>

        {/* Discount Badge - Category-Specific Colors */}
        <Box
          sx={{
            background: categoryConfig.gradient,
            color: "white",
            px: { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 1, sm: 1.25, md: 1.5 },
            borderRadius: { xs: 2, sm: 2.5 },
            display: "inline-flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1 },
            alignSelf: "flex-start",
            mb: { xs: 1.5, sm: 2 },
            boxShadow: `0 4px 12px ${categoryConfig.color}40`,
            border: "1px solid",
            borderColor: transparentColors.white20,
          }}
        >
          <LocalOffer sx={{ fontSize: { xs: 18, sm: 20, md: 22 } }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "0.938rem", sm: "1.063rem", md: "1.125rem" },
              letterSpacing: "0.5px",
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            {offer.discountText}
          </Typography>
        </Box>

        {/* Validity - Compact */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            p: { xs: 0.75, md: 1 },
            borderRadius: 1.5,
            backgroundColor: expiringSoon ? "#fff3e0" : "#f5f5f5",
            mb: offer.viewCount ? 1 : 0,
          }}
        >
          <AccessTime
            sx={{
              fontSize: { xs: 14, md: 16 },
              color: expiringSoon ? "warning.main" : "text.secondary",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: expiringSoon ? "warning.dark" : "text.secondary",
              fontSize: { xs: "0.75rem", md: "0.813rem" },
            }}
          >
            {expiringSoon
              ? `⚡ ${daysRemaining}d left`
              : `Until ${formatDate(validDate)}`}
          </Typography>
        </Box>

        {/* Exclusive Badge (left) and View/Share Counts (right) - Same Line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {/* Exclusive Badge - Left */}
          {offer.tags?.includes("exclusive") && (
            <Chip
              label="EXCLUSIVE"
              size="small"
              icon={<span style={{ color: "#8B4513" }}>⭐</span>}
              sx={{
                backgroundColor: "rgba(255,215,0,0.95)",
                color: "#000",
                fontWeight: 700,
                fontSize: { xs: "0.625rem", md: "0.688rem" },
                height: { xs: 22, md: 24 },
                boxShadow: 2,
                "& .MuiChip-icon": {
                  fontSize: { xs: 12, md: 14 },
                  marginLeft: "4px",
                },
              }}
            />
          )}

          {/* View and Share Counts - Right */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              ml: "auto",
            }}
          >
            {/* View Count */}
            {offer.viewCount !== undefined && offer.viewCount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Visibility
                  sx={{ fontSize: { xs: 14, md: 16 }, color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.75rem", md: "0.813rem" },
                    fontWeight: 500,
                  }}
                >
                  {offer.viewCount.toLocaleString()} view
                  {offer.viewCount !== 1 ? "s" : ""}
                </Typography>
              </Box>
            )}

            {/* Share Count */}
            {offer.shareCount !== undefined && offer.shareCount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <ShareIcon
                  sx={{ fontSize: { xs: 14, md: 16 }, color: "text.secondary" }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: "0.75rem", md: "0.813rem" },
                    fontWeight: 500,
                  }}
                >
                  {offer.shareCount.toLocaleString()} share
                  {offer.shareCount !== 1 ? "s" : ""}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OfferCard;
